import type { ShareCardGateway, ShareCardKind, ShareCardView, ShareChip, ShareReaction } from './share-card-gateway';

/**
 * Everything the public share-card page decides, with no React in it.
 *
 * Extracted for the reason `lib/recipient-error.ts` and `lib/brief-date-key.ts` are: a unit test must
 * not import the app's dependency graph (orion-web CLAUDE.md, "Tests"), and a page component drags in
 * the design system, i18n and the data layer. So the page renders; this file decides — which token
 * shapes are real, which taps a kind offers, what the OG tags may say, and when an open counts.
 */

/**
 * A token is a SHORT token, 12 base62 characters (VMR `sharecard/ShortLinks`, every link minted from 2026-10-31), or
 * `<id>.<secret>` (every link minted before, which keeps working). Shape only — whether it names a live card or round is
 * the backend's word.
 */
const LONG_TOKEN_PATTERN = /^[A-Za-z0-9_-]{8,128}\.[A-Za-z0-9_-]{16,128}$/;
const SHORT_TOKEN_PATTERN = /^[0-9A-Za-z]{12}$/;

export function isShortToken(value: unknown): boolean {
  return typeof value === 'string' && SHORT_TOKEN_PATTERN.test(value);
}

export function isWellFormedToken(value: unknown): boolean {
  return typeof value === 'string' && (SHORT_TOKEN_PATTERN.test(value) || LONG_TOKEN_PATTERN.test(value));
}

/** The token off a Next dynamic route segment, which may arrive as an array. */
export function readRouteToken(raw: string | string[] | undefined): string {
  const value = Array.isArray(raw) ? raw[0] : raw;
  return isWellFormedToken(value) ? (value as string) : '';
}

/**
 * The taps each kind offers. The SERVER holds the same table
 * (`orion-harness-poc/src/share/share-card/share-card-contract.ts`) and refuses anything outside it, so
 * this copy exists to render the right affordances — never to authorize them.
 */
export const REACTIONS_BY_KIND: Record<ShareCardKind, readonly ShareReaction[]> = {
  FILE: ['heart', 'thumbs_up', 'laugh', 'thinking'],
  CHOICE: [],
  TASK: ['heart', 'clap', 'fire', 'wow'],
  PLAN: ['thumbs_up', 'thumbs_down'],
  DASHBOARD: ['heart', 'clap', 'fire'],
};

export const CHIPS_BY_KIND: Record<ShareCardKind, readonly ShareChip[]> = {
  FILE: ['love_it', 'make_it_bolder', 'call_me'],
  CHOICE: [],
  TASK: ['tell_me_more'],
  PLAN: ['works', 'different_day', 'call_me'],
  DASHBOARD: [],
};

export function reactionsFor(kind: ShareCardKind): readonly ShareReaction[] {
  return REACTIONS_BY_KIND[kind] ?? [];
}

export function chipsFor(kind: ShareCardKind): readonly ShareChip[] {
  return CHIPS_BY_KIND[kind] ?? [];
}

/** The glyph a reaction renders as. Not copy — the same in every locale. */
export const REACTION_GLYPH: Record<ShareReaction, string> = {
  heart: '❤️',
  thumbs_up: '👍',
  laugh: '😂',
  thinking: '🤔',
  clap: '👏',
  fire: '🔥',
  wow: '😮',
  thumbs_down: '👎',
};

/** The i18n key for the ask line, which is per kind (design §3): each kind asks a different question. */
export function askKeyFor(kind: ShareCardKind): string {
  return `shareCard.ask.${kind}`;
}

/**
 * The OG / Twitter preview — GENERIC, and that is a security property rather than a style choice.
 *
 * An unfurled preview lands in messenger caches and CDNs that a revoke cannot reach, so the card's
 * content and its media URL must never appear in one: a card revoked an hour later is still legible in
 * a WhatsApp thread forever if we put the image there. What the preview may carry is what the sender
 * already told the recipient by sending it — their name and the KIND of thing.
 *
 * Returns keys and interpolations rather than strings, so the page localizes them the usual way and
 * this function stays testable without i18n.
 */
export interface ShareCardOgMeta {
  titleKey: string;
  descriptionKey: string;
  values: { senderName: string };
  /** Always `noindex` — a share card is addressed to one person, not to the web. */
  robots: 'noindex';
}

export function ogMetaFor(card: Pick<ShareCardView, 'kind' | 'senderName'>): ShareCardOgMeta {
  return {
    titleKey: `shareCard.og.title.${card.kind}`,
    descriptionKey: 'shareCard.og.description',
    values: { senderName: card.senderName },
    robots: 'noindex',
  };
}

/**
 * Bot / prefetch user agents. The page uses this to decide whether to MOUNT the open beacon at all;
 * the endpoint applies the same rule again, because a client can say anything.
 *
 * Kept in step with `isBotUserAgent` in the harness contract. Deliberately duplicated rather than
 * shared: orion-web must not depend on the harness package, and a page that guessed wrong here costs a
 * data point, while the endpoint's copy is the one that protects the signal.
 */
const BOT_UA = /(bot|crawl|spider|slurp|preview|fetcher|facebookexternalhit|whatsapp|telegram|slack|discord|twitter|skype|linkedin|embedly|quora|pinterest|vkshare|outbrain|curl|wget|python-requests|okhttp|headless|lighthouse|monitor|scanner|proofpoint|mimecast|barracuda|symantec)/i;

export function isBotUserAgent(userAgent: string | undefined | null): boolean {
  if (typeof userAgent !== 'string' || !userAgent.trim()) {
    return true;
  }
  return BOT_UA.test(userAgent);
}

/** What the page renders. Two states only — a card, or the one neutral screen. */
export type ShareCardPageProps =
  | { state: 'unavailable' }
  | {
      state: 'ready';
      card: ShareCardView;
      /** Echoed back so the browser can post the beacon and the response. It is already in the URL. */
      token: string;
      /** False for an unfurler or a scanner: render the page, do not mount the beacon. */
      countOpen: boolean;
    };

/**
 * The server render.
 *
 * Note what it does NOT do: it never records an open. An open is the first authoritative delivery
 * signal (design §5), and a prefetcher, a link scanner or our own monitoring would otherwise be the
 * "first device" on every card — corrupting both that signal and the forwarding heuristic built on it.
 * The beacon is fired from the browser, on mount, by a client that ran our JavaScript.
 *
 * Not-found, expired, revoked and held all arrive here as `null` and all render the same screen. That
 * uniformity is the point: a token must not be an oracle for which of those states it is in.
 */
export async function buildShareCardPageProps(params: {
  token: string | string[] | undefined;
  userAgent: string | undefined;
  gateway: ShareCardGateway;
}): Promise<ShareCardPageProps> {
  const token = readRouteToken(params.token);
  if (!token) {
    return { state: 'unavailable' };
  }
  const card = await params.gateway.getByToken({ token });
  if (!card) {
    return { state: 'unavailable' };
  }
  return { state: 'ready', card, token, countOpen: !isBotUserAgent(params.userAgent) };
}

/**
 * This browser's device key — a random id, not an identity.
 *
 * It exists so a second visit from the same phone REPLACES that phone's answer instead of appending a
 * second one, which is what the page's single-selection affordance promises. It says nothing about who
 * the reader is and is never sent anywhere but the four share-card routes.
 *
 * Every access is wrapped: `localStorage` throws in a private window, with site data blocked, and
 * inside some in-app webviews. A reader in one of those still gets a working page — their tap simply
 * cannot be deduped against a later one, which is the right way for this to degrade.
 */
export const DEVICE_KEY_STORAGE_KEY = 'symphony-share-device-key';

export function readOrCreateDeviceKey(storage?: Storage | null): string {
  const random = () => `dk_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  let store: Storage | null | undefined = storage;
  if (store === undefined) {
    try {
      store = typeof window === 'undefined' ? null : window.localStorage;
    } catch {
      store = null;
    }
  }
  if (!store) {
    return random();
  }
  try {
    const existing = store.getItem(DEVICE_KEY_STORAGE_KEY);
    if (existing && /^[A-Za-z0-9_-]{1,64}$/.test(existing)) {
      return existing;
    }
  } catch {
    return random();
  }
  const minted = random();
  try {
    store.setItem(DEVICE_KEY_STORAGE_KEY, minted);
  } catch {
    /* a device that cannot remember still gets to answer */
  }
  return minted;
}
